import {
  ComponentSlot,
  type DomainComponentRegistry,
  type ServerUiComponent,
  ServerUiDomainProvider,
} from '@features/server-ui';
import { useOrganizationId } from '@features/user/store/hooks';
import { Alert, Box, Button, Skeleton } from '@mui/material';
import { SentryErrorBoundary } from '@services/SentryService';
import { useEventTracking } from '@services/statsig';

import { type ArAgingPayload, type CleanClaimsPayload, useRevenueContainerQuery } from './api';
import { ArCard, CleanClaimsCard } from './RevenueCards';
import { useRevenueAccess } from './useRevenueAccess';

function CleanDomain({ component }: { component: ServerUiComponent }) {
  const { jotbillAnalytics } = useEventTracking();
  const data = component.data as unknown as CleanClaimsPayload | undefined;
  return data ? (
    <CleanClaimsCard
      data={data}
      onOpen={() => jotbillAnalytics.revenueClicked({ action: 'open_card', view: 'clean' })}
    />
  ) : null;
}
function ArDomain({ component }: { component: ServerUiComponent }) {
  const { jotbillAnalytics } = useEventTracking();
  const data = component.data as unknown as ArAgingPayload | undefined;
  return data ? (
    <ArCard
      data={data}
      onOpen={() => jotbillAnalytics.revenueClicked({ action: 'open_card', view: 'ar' })}
    />
  ) : null;
}
const registry: DomainComponentRegistry = {
  jotbill_clean_claims: CleanDomain,
  jotbill_ar_aging: ArDomain,
};
const CardSkeleton = () => (
  <Skeleton variant="rounded" height={300} aria-label="Loading revenue card" />
);
const skeletons = { byPrimitive: { domain_payload: CardSkeleton } };

export function RevenueHomeSection() {
  const enabled = useRevenueAccess();
  const organizationId = useOrganizationId() ?? undefined;
  const query = useRevenueContainerQuery(
    { entityId: organizationId },
    {
      skip: !enabled,
      refetchOnMountOrArgChange: true,
    },
  );
  if (!enabled) return null;
  const current = query.currentData?.entity_id === organizationId ? query.currentData : undefined;
  const components = [
    ...(current?.top_level ?? []),
    ...(current?.sections.flatMap(s => s.components) ?? []),
  ];
  return (
    <Box
      component="section"
      className="rw rw-home-section"
      sx={{ mt: 3 }}
      aria-label="Revenue analytics"
    >
      <h2>Revenue analytics</h2>
      <p>Understand payer outcomes and find the next follow-up.</p>
      {query.isError ? (
        <Alert severity="error" action={<Button onClick={() => query.refetch()}>Retry</Button>}>
          Revenue analytics could not load.
        </Alert>
      ) : (
        <ServerUiDomainProvider
          key={organizationId}
          components={registry}
          skeletons={skeletons}
          containerKey="jotbill_revenue"
          entityId={organizationId}
        >
          <div className="rw-home-cards">
            {query.isFetching || !current ? (
              <>
                <CardSkeleton />
                <CardSkeleton />
              </>
            ) : (
              components.map(c => (
                <SentryErrorBoundary key={c.type_key}>
                  <ComponentSlot component={c} />
                </SentryErrorBoundary>
              ))
            )}
          </div>
        </ServerUiDomainProvider>
      )}
    </Box>
  );
}
